import { IPost } from '_interfaces/post.interface';
import { IPostType } from '_interfaces/systemPost.types';
import { IPostStatus } from '_interfaces/types/post.types';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { deleteMediaEntriesByPost } from 'utils/services/fileCabinetService';
import { PostMediaDeleteMode } from 'utils/types/postMediaDelete';
import { atom } from 'jotai';
import { atomFamily, atomWithObservable, atomWithStorage, loadable } from 'jotai/utils';
import { Observable } from 'rxjs';
import { auth, db, refreshFirebaseToken } from 'utils/firebase';
import { selectedCompanyIdAtom } from '../company';
import { selectedProjectAtom } from '../projects';
import { createCrudAtoms } from '../utils/crudFactory';

// Persistent cache for posts data
export const selectionsFeedPostsCacheAtom = atomWithStorage<IPost[]>(
  'selectionsFeedPostsCache',
  []
);
export const localSelectionsFeedDraftPostsAtom = atomWithStorage<Partial<IPost>[]>(
  'localSelectionsFeedDraftPosts',
  []
);

// Real-time Firestore listener for project feed using atomFamily
export const selectionsFeedPostsAtom = atomFamily((selectionsItemId: string) =>
  atomWithObservable<IPost[]>(
    (get) => {
      const project = get(selectedProjectAtom);
      const companyId = get(selectedCompanyIdAtom);

      // Use cached posts synchronously as an immediate first value
      const cached = get(selectionsFeedPostsCacheAtom);
      const initial: IPost[] = cached instanceof Promise ? [] : cached;

      if (!project?.id) {
        return new Observable<IPost[]>((subscriber) => {
          // Emit synchronously to avoid suspension
          subscriber.next(initial);
        });
      }

      const postQuery = query(
        collection(db, `companies/${companyId}/projects/${project.id}/selectionsFeed`),
        where('itemId', '==', selectionsItemId),
        orderBy('createdAt', 'desc')
      );

      return new Observable<IPost[]>((subscriber) => {
        // Emit synchronously so reads never suspend on first access
        subscriber.next(initial);

        const unsubscribe = onSnapshot(postQuery, (snapshot) => {
          const postData: IPost[] = snapshot.docs.map((doc) =>
            IPost.fromDocumentData({ ...doc.data(), id: doc.id })
          );
          subscriber.next(postData);
        });

        return () => {
          unsubscribe();
        };
      });
    },
    {
      // Fallback initial value in case cached is a Promise
      initialValue: [],
    }
  )
);

// Sync latest Firestore data into cache every time postsAtom updates
export const selectionsFeedPostsSyncAtom = atomFamily((selectionsItemId: string) =>
  atom(null, async (get, set) => {
    const loaded = get(loadable(selectionsFeedPostsAtom(selectionsItemId)));
    if (loaded.state === 'hasData') {
      set(selectionsFeedPostsCacheAtom, loaded.data);
    }
    // If loading or hasError, do nothing – avoid triggering Suspense from a write
  })
);

export const addSelectionsFeedPostAtom = atomFamily((selectionsItemId: string) =>
  atom(
    null,
    async (
      get,
      set,
      params: {
        newPost: Partial<IPost>;
        user: { displayName: string; avatarUrl: string; userId: string; role: string };
      }
    ) => {
      // Ensure Firebase authentication is valid
      let firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        console.log('No Firebase user found, attempting to refresh token...');
        const refreshSuccess = await refreshFirebaseToken();
        if (!refreshSuccess) {
          throw new Error('Failed to authenticate with Firebase');
        }
        firebaseUser = auth.currentUser;
      }

      const { newPost, user } = params;
      const project = get(selectedProjectAtom)!;
      const companyId = get(selectedCompanyIdAtom);

      const parentPath = `companies/${companyId}/projects/${project.id}/selectionsFeed`;
      const newDocRef = newPost.id
        ? doc(db, `${parentPath}/${newPost.id}`)
        : doc(collection(db, parentPath));

      const draftPost: Partial<IPost> = {
        id: newDocRef.id,
        projectId: project.id.toString(),
        companyId,
        isDeleted: false,
        isEdited: false,
        itemId: selectionsItemId,
        replyCount: 0,
        createdAt: new Date(),
        message: newPost.message || '',
        mentions: newPost.mentions || [],
        hashtags: newPost.hashtags || [],
        tags: newPost.tags || [],
        phaseIds: newPost.phaseIds || [],
        phaseItemIds: newPost.phaseItemIds || [],
        // visibility: newPost.visibility || 'public',
        likes: [],
        media: newPost.media || [],
        status: IPostStatus.DRAFT,
        postType: IPostType.USER_POST,
        createdBy: {
          userId: user?.userId || '',
          displayName: user?.displayName || '',
          avatarUrl: user?.avatarUrl || '',
          role: user?.role || '',
        },
        parentPath,
        // isLocalDraft: true,
      };

      // Add to local drafts immediately
      set(localSelectionsFeedDraftPostsAtom, (drafts) => [draftPost, ...drafts]);

      try {
        const finalPost = {
          ...draftPost,
          status: IPostStatus.ACTIVE,
          id: newDocRef.id,
          createdAt: serverTimestamp() as any,
          companyId: companyId,
          projectId: project.id.toString(),
          itemId: selectionsItemId,
          isDeleted: false,
          isEdited: false,
          // isLocalDraft: false,
        } as IPost;

        if (finalPost.message !== '<p></p>\n' || finalPost.media.length > 0) {
          await setDoc(newDocRef, finalPost);
          // Remove draft + rely on real-time Firestore update to insert final post
          set(localSelectionsFeedDraftPostsAtom, (drafts) =>
            drafts.filter((d) => d.id !== newDocRef.id)
          );
        }
      } catch (error) {
        console.error('Error creating post:', error);
        set(localSelectionsFeedDraftPostsAtom, (drafts) =>
          drafts.filter((d) => d.id !== newDocRef.id)
        );
        throw error;
      }
    }
  )
);

export const updateSelectionsFeedPostAtom = atom(null, async (get, set, post: Partial<IPost>) => {
  const project = get(selectedProjectAtom)!;
  const companyId = get(selectedCompanyIdAtom);

  const { updateAtom } = createCrudAtoms<IPost>(
    `companies/${companyId}/projects/${project.id}/selectionsFeed`
  );
  await set(updateAtom, { id: post.id, data: post });
});

export const deleteSelectionsFeedPostAtom = atom(
  null,
  async (get, set, id: string, mediaDeleteMode: PostMediaDeleteMode = 'delete') => {
    const project = get(selectedProjectAtom)!;
    const companyId = get(selectedCompanyIdAtom);
    if (!project?.id || !companyId) return;

    // Step 1: Delete Firestore post doc — UI updates instantly via listener
    const { deleteAtom } = createCrudAtoms<IPost>(
      `companies/${companyId}/projects/${project.id}/selectionsFeed`,
    );
    await set(deleteAtom, id);

    // Step 2: Background — delete comments subcollection + FC records (fire-and-forget)
    void (async () => {
      try {
        const commentsPath = `companies/${companyId}/projects/${project.id}/selectionsFeed/${id}/comments`;
        await Promise.allSettled([
          (async () => {
            const commentsSnap = await getDocs(collection(db, commentsPath));
            await Promise.all(commentsSnap.docs.map((commentDoc) => deleteDoc(commentDoc.ref)));
          })(),
          deleteMediaEntriesByPost(project.id, id, mediaDeleteMode),
        ]);
      } catch (err) {
        console.error('[SelectionsFeed] Background delete cleanup failed:', err);
      }
    })();
  },
);

export const toggleSelectionsFeedPostLikeAtom = atom(
  null,
  async (
    get,
    set,
    payload: {
      selectionsItemId: string;
      userId: string;
      postId: string;
    }
  ) => {
    // Check Firebase auth state first
    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error('No Firebase user authenticated');
      throw new Error('User must be authenticated to like posts');
    }

    const { userId, postId, selectionsItemId } = payload;

    if (!postId || !selectionsItemId || !userId) {
      console.error('Post ID, User ID or Selections Item ID missing:', {
        postId,
        selectionsItemId,
        userId,
      });
      throw new Error('Post ID, User ID or Selections Item ID missing');
    }

    // Check what's in the cache
    const allPosts = get(selectionsFeedPostsAtom(selectionsItemId));

    const post = allPosts.find((p) => p.id === postId);

    if (!post) {
      console.error('Post not found with ID:', postId);
      console.error('Error getting post from Firestore:', postId);
      return;
    }

    const hasLiked = post.likes?.includes(userId) ?? false;
    const newlikes = hasLiked
      ? post.likes?.filter((uid) => uid !== userId)
      : [...(post.likes || []), userId];

    // Use direct Firestore update instead of createCrudAtoms
    try {
      await set(updateSelectionsFeedPostAtom, { ...post, likes: newlikes });

      // Update the cache to reflect the change
      set(selectionsFeedPostsCacheAtom, (current) =>
        current.map((p) => (p.id === postId ? ({ ...p, likes: newlikes } as IPost) : p))
      );
    } catch (error) {
      console.error('Error updating Firestore:', error);
      throw error;
    }
  }
);
